import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeftRight, Plus, Trash2, X, Save, AlertTriangle, Loader2 } from 'lucide-react';
import { apiService } from '../../services/apiService';

export default function UomAdmin() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newVariant, setNewVariant] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const newVariantRef = useRef(null);

  async function loadGroups() {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getUomGroups();
      setGroups(data.groups || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function saveGroup() {
    if (!editingGroup) return;
    const { canonical, description, variants, isNew } = editingGroup;
    if (!canonical.trim()) return;
    setSaving(canonical);
    setError(null);
    try {
      await apiService.saveUomGroup(canonical.trim().toUpperCase(), description.trim(), variants);
      setEditingGroup(null);
      setNewVariant('');
      await loadGroups();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  }

  async function deleteGroup(canonical) {
    setError(null);
    try {
      await apiService.deleteUomGroup(canonical);
      setDeleteConfirm(null);
      setEditingGroup(null);
      await loadGroups();
    } catch (err) {
      setError(err.message);
    }
  }

  function startEdit(group) {
    setEditingGroup({
      canonical: group.canonical,
      description: group.description || '',
      variants: [...group.variants],
      isNew: false,
    });
    setNewVariant('');
    setDeleteConfirm(null);
  }

  function startNew() {
    setEditingGroup({
      canonical: '',
      description: '',
      variants: [],
      isNew: true,
    });
    setNewVariant('');
    setDeleteConfirm(null);
  }

  function cancelEdit() {
    setEditingGroup(null);
    setNewVariant('');
  }

  function addVariant() {
    const v = newVariant.trim().toUpperCase();
    if (!v || !editingGroup) return;
    if (editingGroup.variants.includes(v)) {
      setNewVariant('');
      return;
    }
    setEditingGroup(prev => ({
      ...prev,
      variants: [...prev.variants, v],
    }));
    setNewVariant('');
    // Focus back on the input after adding
    setTimeout(() => newVariantRef.current?.focus(), 0);
  }

  function removeVariant(variant) {
    if (!editingGroup) return;
    // Don't allow removing the canonical code itself
    if (variant === editingGroup.canonical) return;
    setEditingGroup(prev => ({
      ...prev,
      variants: prev.variants.filter(v => v !== variant),
    }));
  }

  useEffect(() => {
    loadGroups();
  }, []);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-sky-400 animate-spin" />
          <span className="ml-3 text-slate-400">Loading UOM groups...</span>
        </div>
      </div>
    );
  }

  const isEditingExisting = editingGroup && !editingGroup.isNew;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ArrowLeftRight className="w-5 h-5 text-sky-400" />
            <h2 className="text-lg font-semibold text-white">UOM Equivalences</h2>
          </div>
          <p className="text-sm text-slate-400">
            Define which units of measure are equivalent. Each group has a canonical code and variant aliases.
          </p>
        </div>
        {!editingGroup && (
          <button
            onClick={startNew}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 hover:from-amber-300 hover:to-orange-400 transition-all"
          >
            <Plus className="w-4 h-4" />
            Add Group
          </button>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-red-400 text-sm">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* New group form */}
      {editingGroup?.isNew && (
        <div className="mb-6 bg-slate-800 border border-sky-500/50 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white mb-4">New Equivalence Group</h3>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Canonical Code</label>
              <input
                type="text"
                value={editingGroup.canonical}
                onChange={e => setEditingGroup(prev => ({ ...prev, canonical: e.target.value.toUpperCase() }))}
                placeholder="e.g. EA"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white font-mono text-sm focus:border-sky-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Description</label>
              <input
                type="text"
                value={editingGroup.description}
                onChange={e => setEditingGroup(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g. Each / Unit"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:border-sky-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Variant tags */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Variants</label>
            <div className="flex flex-wrap gap-2 min-h-[32px]">
              {editingGroup.variants.map(v => (
                <span
                  key={v}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-700 text-white text-sm font-mono rounded-lg"
                >
                  {v}
                  {v !== editingGroup.canonical && (
                    <button
                      onClick={() => removeVariant(v)}
                      className="text-slate-400 hover:text-red-400 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>

          {/* Add variant input */}
          <div className="flex gap-2 mb-4">
            <input
              ref={newVariantRef}
              type="text"
              value={newVariant}
              onChange={e => setNewVariant(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addVariant(); } }}
              placeholder="Add variant (e.g. EACH, UN)"
              className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white font-mono text-sm focus:border-sky-500 focus:outline-none"
            />
            <button
              onClick={addVariant}
              disabled={!newVariant.trim()}
              className="px-3 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <button
              onClick={cancelEdit}
              className="px-4 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={saveGroup}
              disabled={!editingGroup.canonical.trim() || saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-sky-500 hover:bg-sky-400 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </button>
          </div>
        </div>
      )}

      {/* Existing groups list */}
      {groups.length === 0 && !editingGroup ? (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-8 text-center">
          <ArrowLeftRight className="w-8 h-8 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No UOM equivalence groups defined yet.</p>
          <p className="text-slate-500 text-xs mt-1">Click "Add Group" to create your first group.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map(group => {
            const isEditing = isEditingExisting && editingGroup.canonical === group.canonical;

            if (isEditing) {
              // Edit mode for existing group
              return (
                <div key={group.canonical} className="bg-slate-800 border border-sky-500/50 rounded-xl p-5">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">Canonical Code</label>
                      <span className="inline-block px-3 py-1 bg-sky-500/20 text-sky-400 font-mono font-bold text-sm rounded-lg">
                        {editingGroup.canonical}
                      </span>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">Description</label>
                      <input
                        type="text"
                        value={editingGroup.description}
                        onChange={e => setEditingGroup(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="e.g. Each / Unit"
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:border-sky-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* Variant tags */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Variants</label>
                    <div className="flex flex-wrap gap-2 min-h-[32px]">
                      {editingGroup.variants.map(v => (
                        <span
                          key={v}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-700 text-white text-sm font-mono rounded-lg"
                        >
                          {v}
                          {v !== editingGroup.canonical && (
                            <button
                              onClick={() => removeVariant(v)}
                              className="text-slate-400 hover:text-red-400 transition-colors"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Add variant input */}
                  <div className="flex gap-2 mb-4">
                    <input
                      ref={newVariantRef}
                      type="text"
                      value={newVariant}
                      onChange={e => setNewVariant(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addVariant(); } }}
                      placeholder="Add variant (e.g. EACH, UN)"
                      className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white font-mono text-sm focus:border-sky-500 focus:outline-none"
                    />
                    <button
                      onClick={addVariant}
                      disabled={!newVariant.trim()}
                      className="px-3 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={cancelEdit}
                      className="px-4 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveGroup}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-sky-500 hover:bg-sky-400 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving === editingGroup.canonical ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Save
                    </button>
                  </div>
                </div>
              );
            }

            // View mode
            return (
              <div key={group.canonical} className="bg-slate-800 border border-slate-700 rounded-xl p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1.5">
                    <span className="px-3 py-1 bg-sky-500/20 text-sky-400 font-mono font-bold text-sm rounded-lg">
                      {group.canonical}
                    </span>
                    {group.description && (
                      <span className="text-sm text-slate-400">{group.description}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {group.variants
                      .filter(v => v !== group.canonical)
                      .map(v => (
                        <span
                          key={v}
                          className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs font-mono rounded"
                        >
                          {v}
                        </span>
                      ))}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {deleteConfirm === group.canonical ? (
                    <span className="flex items-center gap-2 text-sm">
                      <span className="text-red-400">Delete?</span>
                      <button
                        onClick={() => deleteGroup(group.canonical)}
                        className="px-2 py-1 text-xs font-medium text-red-400 hover:text-white hover:bg-red-500/20 rounded transition-colors"
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="px-2 py-1 text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
                      >
                        No
                      </button>
                    </span>
                  ) : (
                    <>
                      <button
                        onClick={() => startEdit(group)}
                        disabled={!!editingGroup}
                        className="px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(group.canonical)}
                        disabled={!!editingGroup}
                        className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
